import type { ReactNode } from "react";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import { useIsPhone } from "../hooks/useIsPhone";
import { PhoneFilterBar } from "./phone/PhoneFilterBar";

/**
 * One mode of the two-stage filter — e.g. "Filter by role" plus the role
 * options that show in the conditional second dropdown when the mode is
 * active.
 */
export interface TwoStageFilterMode<V extends string> {
  /** Mode key — value of the first dropdown. */
  value: string;
  /** Label shown in the first dropdown for this mode. */
  label: string;
  /** Options for the conditional second dropdown when this mode is active. */
  options: Array<{ value: V; label: string }>;
  /** Placeholder shown in the second dropdown's empty state. */
  placeholder: string;
  /** Optional minimum width override for the second dropdown. Default 150px. */
  valueMinWidth?: number;
}

export interface TwoStageFilterProps<V extends string> {
  /** Each mode renders an item in the first dropdown. */
  modes: TwoStageFilterMode<V>[];
  /** Mode key for the "no filter" option. Default "none". */
  noneValue?: string;
  /** Label for the "no filter" option. Default "All". */
  noneLabel?: string;
  /** Free-text search input on the left. */
  search: string;
  onSearchChange: (next: string) => void;
  searchPlaceholder?: string;
  searchMinWidth?: number;
  searchTestId?: string;
  /** Currently-active mode key. */
  mode: string;
  /** Currently-selected value within the active mode. */
  value: V | null;
  /**
   * Fires when either the mode changes (selecting a new mode resets the value
   * to null) or the value within the active mode changes.
   */
  onChange: (next: { mode: string; value: V | null }) => void;
  /** Trailing slot, rendered to the right (e.g. a bulk-action button). */
  trailing?: ReactNode;
  modeTestId?: string;
  valueTestId?: string;
}

/**
 * Two-stage filter pattern: free-text search on the left, mode picker in the
 * middle, conditional value picker on the right. Used by the Users and
 * Attendees pages to keep their filter UX in sync — extracted from the
 * Attendees page in the rework that unified Users + Attendees admin pages.
 *
 * Selecting a new mode automatically resets the value to null. Callers don't
 * need to track the mode + value separately; the component fires a single
 * `onChange({ mode, value })` whenever either changes.
 */
export function TwoStageFilter<V extends string>(props: TwoStageFilterProps<V>) {
  const isPhone = useIsPhone();
  if (isPhone) return <PhoneFilterBar {...props} />;
  return <DesktopTwoStageFilter {...props} />;
}

function DesktopTwoStageFilter<V extends string>(props: TwoStageFilterProps<V>) {
  const {
    modes,
    noneValue = "none",
    noneLabel = "All",
    search,
    onSearchChange,
    searchPlaceholder = "Search by name or email...",
    searchMinWidth = 280,
    searchTestId,
    mode,
    value,
    onChange,
    trailing,
    modeTestId,
    valueTestId,
  } = props;

  const activeMode = modes.find((m) => m.value === mode);

  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      sx={{ mb: 3, flexWrap: "wrap", rowGap: 1.5 }}
    >
      <TextField
        size="small"
        placeholder={searchPlaceholder}
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        sx={{ minWidth: searchMinWidth }}
        slotProps={
          searchTestId
            ? { htmlInput: { "data-testid": searchTestId } }
            : undefined
        }
      />

      <FormControl size="small" sx={{ minWidth: 180 }}>
        <Select
          value={mode}
          onChange={(e) =>
            onChange({ mode: e.target.value as string, value: null })
          }
          data-testid={modeTestId}
        >
          <MenuItem value={noneValue}>{noneLabel}</MenuItem>
          {modes.map((m) => (
            <MenuItem key={m.value} value={m.value}>
              {m.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {activeMode && (
        <FormControl
          size="small"
          sx={{ minWidth: activeMode.valueMinWidth ?? 150 }}
        >
          <Select
            value={value ?? ""}
            onChange={(e) =>
              onChange({
                mode,
                value: ((e.target.value as string) || null) as V | null,
              })
            }
            displayEmpty
            data-testid={valueTestId}
          >
            <MenuItem value="" disabled>
              {activeMode.placeholder}
            </MenuItem>
            {activeMode.options.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {trailing && <Stack sx={{ ml: "auto" }}>{trailing}</Stack>}
    </Stack>
  );
}
